import { useCallback, useMemo, useState } from 'react';

import debounce from 'lodash/debounce';

import { email, required, runValidations } from '@coursera/coursera-ui/lib/utils/validation';

import { checkForcedSsoLogin } from 'bundles/third-party-auth/api';
import { type CheckForcedSsoLoginResponse } from 'bundles/third-party-auth/types/responseTypes';

export const INPUT_DEBOUNCE_TIME = 300;

export type EnterpriseSsoLoginInfo = {
  isEnterpriseSsoLogin: boolean;
  redirectUrl: string;
  isLoading: boolean;
  isRedirecting: boolean;
};

const enterpriseSsoLoginInfoInitialState: EnterpriseSsoLoginInfo = {
  isEnterpriseSsoLogin: false,
  redirectUrl: '',
  isLoading: false,
  isRedirecting: false,
};

type UseEnterpriseSsoEmailCheckOptions = {
  onEnforcedSSO?: () => void;
  preferredOrgIds?: string[];
  returnTo?: string;
};

type UseEnterpriseSsoEmailCheckReturn = {
  enterpriseSsoLoginInfo: EnterpriseSsoLoginInfo;
  onEmailChange: (email: string) => void;
  checkForceSSO: (email: string) => Promise<void>;
  onContinueWithSso: () => void;
  resetEnterpriseSsoInfo: () => void;
};

const useEnterpriseSsoEmailCheck = (
  options: UseEnterpriseSsoEmailCheckOptions = {}
): UseEnterpriseSsoEmailCheckReturn => {
  const [enterpriseSsoLoginInfo, setEnterpriseSsoLoginInfo] = useState<EnterpriseSsoLoginInfo>(
    enterpriseSsoLoginInfoInitialState
  );

  const resetEnterpriseSsoInfo = useCallback<VoidFunction>(() => {
    setEnterpriseSsoLoginInfo(enterpriseSsoLoginInfoInitialState);
  }, []);

  const setIsRedirecting = useCallback<(x: boolean) => void>((isRedirecting: boolean) => {
    setEnterpriseSsoLoginInfo((state) => ({ ...state, isRedirecting }));
  }, []);

  const checkForceSSO = useCallback<(emailField: string) => Promise<void>>(
    (emailField: string) => {
      const errors = runValidations(emailField, [required, email]);
      if (!errors) {
        setEnterpriseSsoLoginInfo((state) => ({ ...state, isLoading: true }));
        return Promise.resolve(checkForcedSsoLogin(emailField, options.returnTo, options))
          .then((response: CheckForcedSsoLoginResponse) => {
            if (response.isForceSSOLogin) {
              setEnterpriseSsoLoginInfo((state) => ({
                ...state,
                isEnterpriseSsoLogin: true,
                isLoading: false,
                redirectUrl: response.redirectUrl,
              }));
            } else {
              setEnterpriseSsoLoginInfo(enterpriseSsoLoginInfoInitialState);
            }
          })
          .catch(() => {
            setEnterpriseSsoLoginInfo(enterpriseSsoLoginInfoInitialState);
          });
      } else {
        setEnterpriseSsoLoginInfo(enterpriseSsoLoginInfoInitialState);
        return Promise.resolve();
      }
    },
    [options]
  );

  const debouncedCheckForceSSO = useMemo(() => debounce(checkForceSSO, INPUT_DEBOUNCE_TIME), [checkForceSSO]);

  const onEmailChange = useCallback<(email: string) => void>(
    (emailInput) => {
      debouncedCheckForceSSO(emailInput);
      return () => debouncedCheckForceSSO.cancel();
    },
    [debouncedCheckForceSSO]
  );

  const onContinueWithSso = useCallback<VoidFunction>(() => {
    setIsRedirecting(true);
    window.location.replace(enterpriseSsoLoginInfo.redirectUrl);
  }, [enterpriseSsoLoginInfo.redirectUrl, setIsRedirecting]);

  return {
    enterpriseSsoLoginInfo,
    onEmailChange,
    checkForceSSO,
    onContinueWithSso,
    resetEnterpriseSsoInfo,
  };
};

export default useEnterpriseSsoEmailCheck;
